import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Briefcase, Download, Eye, Trash2, Check, X, Clock,
  Mail, Phone, Calendar, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import './AdminCandidaturas.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCandidaturas = () => {
  const { token } = useAuth();
  const [candidaturas, setCandidaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadCandidaturas();
  }, []);

  const loadCandidaturas = async () => {
    try {
      const response = await axios.get(`${API}/submissions/candidaturas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidaturas(response.data);
    } catch (error) {
      console.error('Erro ao carregar candidaturas:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/submissions/candidaturas/${id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidaturas(prev => 
        prev.map(c => c.id === id ? { ...c, status: newStatus } : c)
      );
    } catch (error) {
      console.error('Erro ao atualizar estado:', error);
      alert('Erro ao atualizar estado');
    }
  };

  const deleteCandidatura = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta candidatura?')) return;
    
    try {
      await axios.delete(`${API}/submissions/candidaturas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidaturas(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Erro ao eliminar:', error);
      alert('Erro ao eliminar candidatura');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'novo': return '#2196f3';
      case 'em_analise': return '#ff9800';
      case 'entrevista': return '#9c27b0';
      case 'aceite': return '#4caf50';
      case 'rejeitado': return '#f44336';
      default: return '#757575';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'novo': return 'Novo';
      case 'em_analise': return 'Em Análise';
      case 'entrevista': return 'Entrevista';
      case 'aceite': return 'Aceite';
      case 'rejeitado': return 'Rejeitado';
      default: return status;
    }
  };

  const filteredCandidaturas = filter === 'all' 
    ? candidaturas 
    : candidaturas.filter(c => c.status === filter);

  const statusCounts = {
    all: candidaturas.length,
    novo: candidaturas.filter(c => c.status === 'novo').length,
    em_analise: candidaturas.filter(c => c.status === 'em_analise').length,
    entrevista: candidaturas.filter(c => c.status === 'entrevista').length,
    aceite: candidaturas.filter(c => c.status === 'aceite').length,
    rejeitado: candidaturas.filter(c => c.status === 'rejeitado').length,
  };

  if (loading) {
    return <div className="loading">A carregar candidaturas...</div>;
  }

  return (
    <div className="admin-candidaturas" data-testid="admin-candidaturas">
      <div className="page-header">
        <h1><Briefcase size={24} /> Gestão de Candidaturas</h1>
        <p>Gerir candidaturas recebidas através do website</p>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Todas ({statusCounts.all})
        </button>
        <button 
          className={`filter-btn ${filter === 'novo' ? 'active' : ''}`}
          onClick={() => setFilter('novo')}
        >
          <span className="status-dot" style={{ background: '#2196f3' }}></span>
          Novas ({statusCounts.novo})
        </button>
        <button 
          className={`filter-btn ${filter === 'em_analise' ? 'active' : ''}`}
          onClick={() => setFilter('em_analise')}
        >
          <span className="status-dot" style={{ background: '#ff9800' }}></span>
          Em Análise ({statusCounts.em_analise})
        </button>
        <button 
          className={`filter-btn ${filter === 'entrevista' ? 'active' : ''}`}
          onClick={() => setFilter('entrevista')}
        >
          <span className="status-dot" style={{ background: '#9c27b0' }}></span>
          Entrevista ({statusCounts.entrevista})
        </button>
        <button 
          className={`filter-btn ${filter === 'aceite' ? 'active' : ''}`}
          onClick={() => setFilter('aceite')}
        >
          <span className="status-dot" style={{ background: '#4caf50' }}></span>
          Aceites ({statusCounts.aceite})
        </button>
        <button 
          className={`filter-btn ${filter === 'rejeitado' ? 'active' : ''}`}
          onClick={() => setFilter('rejeitado')}
        >
          <span className="status-dot" style={{ background: '#f44336' }}></span>
          Rejeitadas ({statusCounts.rejeitado})
        </button>
      </div>

      {/* Candidaturas List */}
      {filteredCandidaturas.length === 0 ? (
        <div className="empty-state">
          <Briefcase size={48} color="#ccc" />
          <p>Sem candidaturas {filter !== 'all' ? 'neste estado' : ''}</p>
        </div>
      ) : (
        <div className="candidaturas-list">
          {filteredCandidaturas.map(candidatura => (
            <div key={candidatura.id} className="candidatura-card">
              <div 
                className="candidatura-header"
                onClick={() => setExpandedId(expandedId === candidatura.id ? null : candidatura.id)}
              >
                <div className="candidatura-main-info">
                  <h3>{candidatura.nome_completo}</h3>
                  <span className="posicao-badge">{candidatura.posicao}</span>
                </div>
                <div className="candidatura-meta">
                  <span 
                    className="status-badge"
                    style={{ background: getStatusColor(candidatura.status) }}
                  >
                    {getStatusLabel(candidatura.status)}
                  </span>
                  <span className="date">
                    <Calendar size={14} />
                    {formatDate(candidatura.created_at)}
                  </span>
                  {expandedId === candidatura.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {expandedId === candidatura.id && (
                <div className="candidatura-details">
                  <div className="details-grid">
                    <div className="detail-item">
                      <Mail size={16} />
                      <a href={`mailto:${candidatura.email}`}>{candidatura.email}</a>
                    </div>
                    <div className="detail-item">
                      <Phone size={16} />
                      <a href={`tel:${candidatura.telefone}`}>{candidatura.telefone}</a>
                    </div>
                    <div className="detail-item">
                      <Clock size={16} />
                      <span>Experiência: {candidatura.anos_experiencia || 'Não especificado'}</span>
                    </div>
                    <div className="detail-item">
                      <Calendar size={16} />
                      <span>Disponibilidade: {candidatura.disponibilidade || 'Não especificado'}</span>
                    </div>
                  </div>

                  {candidatura.mensagem && (
                    <div className="mensagem-section">
                      <h4>Mensagem / Motivação</h4>
                      <p>{candidatura.mensagem}</p>
                    </div>
                  )}

                  {candidatura.cv_url && (
                    <div className="cv-section">
                      <a 
                        href={`${BACKEND_URL}${candidatura.cv_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="cv-download-btn"
                      >
                        <FileText size={18} />
                        Download CV
                      </a>
                    </div>
                  )}

                  <div className="actions-section">
                    <div className="status-actions">
                      <span>Alterar estado:</span>
                      <button 
                        className="status-action-btn"
                        style={{ borderColor: '#ff9800', color: '#ff9800' }}
                        onClick={() => updateStatus(candidatura.id, 'em_analise')}
                        disabled={candidatura.status === 'em_analise'}
                      >
                        Em Análise
                      </button>
                      <button 
                        className="status-action-btn"
                        style={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                        onClick={() => updateStatus(candidatura.id, 'entrevista')}
                        disabled={candidatura.status === 'entrevista'}
                      >
                        Entrevista
                      </button>
                      <button 
                        className="status-action-btn"
                        style={{ borderColor: '#4caf50', color: '#4caf50' }}
                        onClick={() => updateStatus(candidatura.id, 'aceite')}
                        disabled={candidatura.status === 'aceite'}
                      >
                        <Check size={14} /> Aceitar
                      </button>
                      <button 
                        className="status-action-btn"
                        style={{ borderColor: '#f44336', color: '#f44336' }}
                        onClick={() => updateStatus(candidatura.id, 'rejeitado')}
                        disabled={candidatura.status === 'rejeitado'}
                      >
                        <X size={14} /> Rejeitar
                      </button>
                    </div>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteCandidatura(candidatura.id)}
                    >
                      <Trash2 size={16} />
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCandidaturas;
